import React, { useEffect, useState } from "react";
import {
  Users,
  FileText,
  BookOpen,
  TrendingUp,
  Activity,
  DollarSign,
} from "lucide-react";
import { totalUsers } from "../../services/UserService";
import { totalExams } from "../../services/ExamService";
import { totalDocuments } from "../../services/DocumentService";
import { getCourseStats } from "../../services/CourseService";
import { toast } from "react-toastify";

const StatCard = ({ icon: Icon, title, value, color, gradient, subtitle }) => (
  <div
    className={`bg-gradient-to-br ${gradient} rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-white/20`}
  >
    <div className="flex items-center justify-between mb-4">
      <div
        className={`w-14 h-14 rounded-xl ${color} bg-white/20 backdrop-blur-sm flex items-center justify-center`}
      >
        <Icon className="w-7 h-7 text-white" />
      </div>
      <div className="text-right">
        {subtitle && (
          <p className="text-xs text-white/80 font-medium mb-1">{subtitle}</p>
        )}
      </div>
    </div>
    <div>
      <h3 className="text-sm text-white/90 font-medium mb-1">{title}</h3>
      <p className="text-3xl font-bold text-white">{value}</p>
    </div>
  </div>
);

const MiniStatCard = ({ title, value, color }) => (
  <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100 hover:shadow-lg transition-all">
    <p className="text-xs text-gray-500 font-medium mb-1">{title}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalExams: 0,
    totalDocuments: 0,
    totalCourses: 0,
    courseStats: {
      total: 0,
      totalStudents: 0,
      revenue: 0,
      types: {},
    },
  });

  const handleFetch = async () => {
    try {
      setLoading(true);
      const [usersRes, examsRes, documentsRes, coursesRes] = await Promise.all([
        totalUsers(),
        totalExams(),
        totalDocuments(),
        getCourseStats(),
      ]);

      setStats({
        totalUsers: usersRes?.data || 0,
        totalExams: examsRes?.data || 0,
        totalDocuments: documentsRes?.data || 0,
        totalCourses: coursesRes?.data?.total || 0,
        courseStats: coursesRes?.data || {
          total: 0,
          totalStudents: 0,
          revenue: 0,
          types: {},
        },
      });
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message || "Lỗi khi tải dữ liệu dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFetch();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(price || 0);
  };

  if (loading) {
    return (
      <div className="p-[30px] overflow-auto min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-gray-800">Dashboard</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-6 shadow-lg animate-pulse"
              >
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-4" />
                <div className="h-8 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-100px)] p-[30px] overflow-auto bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-4xl font-bold mb-2 text-gray-800">Dashboard</h2>
          <p className="text-gray-600">Tổng quan hệ thống và thống kê</p>
        </div>

        {/* Main Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={Users}
            title="Tổng người dùng"
            value={stats.totalUsers}
            color="bg-blue-500"
            gradient="from-blue-500 to-blue-600"
          />
          <StatCard
            icon={FileText}
            title="Tổng đề thi"
            value={stats.totalExams}
            color="bg-green-500"
            gradient="from-green-500 to-green-600"
          />
          <StatCard
            icon={BookOpen}
            title="Tổng tài liệu"
            value={stats.totalDocuments}
            color="bg-purple-500"
            gradient="from-purple-500 to-purple-600"
          />
          <StatCard
            icon={Activity}
            title="Tổng khóa học"
            value={stats.totalCourses}
            color="bg-orange-500"
            gradient="from-orange-500 to-orange-600"
          />
        </div>

        {/* Course Statistics */}
        {stats.courseStats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <h3 className="text-sm text-gray-500 font-medium mb-1">
                Tổng học viên
              </h3>
              <p className="text-3xl font-bold text-gray-800">
                {stats.courseStats.totalStudents || 0}
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <h3 className="text-sm text-gray-500 font-medium mb-1">
                Doanh thu
              </h3>
              <p className="text-3xl font-bold text-gray-800">
                {formatPrice(stats.courseStats.revenue || 0)}
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Activity className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <h3 className="text-sm text-gray-500 font-medium mb-1">
                Loại khóa học
              </h3>
              <div className="space-y-2 mt-3">
                {Object.entries(stats.courseStats.types || {}).map(
                  ([type, count]) => (
                    <div
                      key={type}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-gray-600">{type}</span>
                      <span className="font-semibold text-gray-800">
                        {count}
                      </span>
                    </div>
                  )
                )}
                {Object.keys(stats.courseStats.types || {}).length === 0 && (
                  <p className="text-sm text-gray-400">Chưa có dữ liệu</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
      </div>
    </div>
  );
}
